import { AGENT_DEPLOY_MODE } from '@config/constants';
import {
  BasePageModel,
  basePageToLocal,
  Attribute,
  DataQuery,
  FrameItem,
  attributeToLocal,
  dataQueryToLocal,
  FrameItemToLocal,
  ProviderType,
  ExternalType,
  externalTypeToLocal,
  externalTypeToServer,
  ServiceSlot,
  serviceSlotToLocal,
  serviceSlotToServer
} from '@interface/index';

export interface DbConfig {
  url: string;
  user: string;
  password: string;
  driver: string;
}

export interface KeyValuePair {
  key: string;
  value: any;
}

export interface WebhookConfig {
  url: string;
  pairs: KeyValuePair[];
}

export type GoogleSheetsConfig = {
  spreadsheetId: string;
  credential: string;
};

export type NativeConfig = {
  providerClassName: string;
  configurationMeta: string;
  dependency?: string;
};

export type ServiceInfo = BasePageModel & {
  isInterface: boolean;
  isImplementation?: boolean;
  attributes: Attribute[];
  slotPosition: {
    pageId: string;
  }[];
  nestedSlotCustomizations: Attribute[];
  dataQueries: DataQuery[];
  services: ServiceSlot[];
  inheritedFrames: FrameItem[];
  qualification: string;
  externalQualification: boolean;
  psql: Boolean;
  providerType: ProviderType;
  deployMode?: AGENT_DEPLOY_MODE;
  sqlConfig?: DbConfig;
  webhookConfig?: WebhookConfig;
  googleSheetsConfig?: GoogleSheetsConfig;
  nativeConfig?: NativeConfig;
  externalTypes: ExternalType[];
  masterSelf?: ServiceInfo;
};

export function serviceInfoToLocal(data: any): ServiceInfo {
  return {
    ...basePageToLocal(data),
    isInterface: data.is_interface,
    isImplementation: data.is_impelementation,
    attributes: data.attributes
      && data.attributes.map(attributeToLocal),
    nestedSlotCustomizations: data.nested_slot_customizations && data.nested_slot_customizations.map(attributeToLocal),
    dataQueries: data.data_queries
      && data.data_queries.map(dataQueryToLocal),
    inheritedFrames: data.inherited_frames
      && data.inherited_frames.map(FrameItemToLocal),
    qualification: data.qualification,
    externalQualification: data.external_qualification,
    slotPosition: data.slot_position && data.slot_position.map((item: any) => ({
      pageId: item.page_id
    })) || [],
    services: (data.services || []).map(serviceSlotToLocal),
    psql: data.psql,
    providerType: data.provider_type || ProviderType.Unknown,
    sqlConfig: data.sql_config && {
      url: data.sql_config.url,
      user: data.sql_config.user,
      password: data.sql_config.password,
      driver: data.sql_config.driver
    },
    webhookConfig: data.webhook_config && {
      url: data.webhook_config.url,
      pairs: data.webhook_config.pairs &&
        data.webhook_config.pairs.map((pair: any) => ({
          key: pair.key,
          value: pair.value
        })) || []
    },
    googleSheetsConfig: data.google_sheets_config && {
      spreadsheetId: data.google_sheets_config.spreadsheet_id,
      credential: data.google_sheets_config.credential
    },
    nativeConfig: data.native_config && {
      providerClassName: data.native_config.provider_class_name,
      configurationMeta: data.native_config.configuration_meta,
      dependency: data.native_config.dependency
    },
    externalTypes: data.external_types
      && data.external_types.map(externalTypeToLocal),
    masterSelf: data.master_self && serviceInfoToLocal(data.master_self)
  };
}

export function serviceInfoToServer(data: ServiceInfo) {
  return {
    id: data.id,
    name: data.name,
    comment: data.comment,
    is_interface: data.isInterface,
    is_implementation: data.isImplementation,
    qualification: data.qualification,
    external_qualification: data.externalQualification,
    psql: data.psql,
    provider_type: data.providerType,
    services: (data.services || []).map(serviceSlotToServer),
    sql_config: data.sqlConfig && {
      url: data.sqlConfig.url,
      user: data.sqlConfig.user,
      password: data.sqlConfig.password,
      driver: data.sqlConfig.driver
    },
    webhook_config: data.webhookConfig && {
      url: data.webhookConfig.url,
      pairs: data.webhookConfig.pairs &&
        data.webhookConfig.pairs.map((pair: KeyValuePair) =>
        ({
          key: pair.key,
          value: pair.value
        })) || []
    },
    google_sheets_config: data.googleSheetsConfig && {
      spreadsheet_id: data.googleSheetsConfig.spreadsheetId,
      credential: data.googleSheetsConfig.credential
    },
    native_config: data.nativeConfig && {
      provider_class_name: data.nativeConfig.providerClassName,
      configuration_meta: data.nativeConfig.configurationMeta,
      dependency: data.nativeConfig.dependency
    },
    external_types: data.externalTypes &&
      data.externalTypes.map(externalTypeToServer)
  };
}
